import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';

import { Blueprint } from '../../models/blueprint.model';
import { BlueprintEditorComponent } from './blueprint-editor/blueprint-editor.component';
import { BlueprintItemViewerComponent } from '../blueprints-viewer/blueprint-item-viewer/blueprint-item-viewer.component';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { QuotesService } from '../quotes.service';
import {
  BlueprintItem,
  BlueprintItemWrite,
} from '../../models/blueprint-item.model';
import { Subscription } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { isPermissionError, parseApiError } from '../quotes-api-error';

@Component({
  selector: 'app-blueprints-page',
  imports: [
    BlueprintEditorComponent,
    BlueprintItemViewerComponent,
    Button,
    DatePipe,
    Tooltip,
    SharedConfirmDialogComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './blueprints-page.component.html',
  styleUrl: './blueprints-page.component.scss',
})
export class BlueprintsPageComponent implements OnInit, OnDestroy {
  quotesService = inject(QuotesService);
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);

  showBlueprintEditor: boolean = false;
  editingBlueprint: Blueprint | null = null;
  allBlueprints: Blueprint[] = [];
  expandedRows = new Set<string>();

  private subscriptions = new Subscription();

  get blueprintMap(): Map<string, Blueprint> {
    const map = new Map<string, Blueprint>();
    for (const bp of this.allBlueprints) {
      map.set(String(bp.id), bp);
    }
    return map;
  }

  toggleRow(id: string): void {
    if (this.expandedRows.has(id)) {
      this.expandedRows.delete(id);
    } else {
      this.expandedRows.add(id);
    }
  }

  getRootItems(blueprint: Blueprint): BlueprintItem[] {
    if (!blueprint.items || blueprint.items.length === 0) {
      return [];
    }
    return blueprint.items
      .filter((item) => item.parent_blueprint_item_id === null)
      .sort((a, b) => a.sibling_order - b.sibling_order);
  }

  ngOnInit(): void {
    this.loadBlueprints();
  }

  private loadBlueprints(): void {
    this.subscriptions.add(
      this.quotesService.getBlueprints$().subscribe({
        next: (blueprints: Blueprint[]) => {
          this.allBlueprints = blueprints;
        },
        error: (error: unknown) => {
          if (isPermissionError(error)) return;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: parseApiError(error, 'Failed to load blueprints'),
          });
          console.error('Error loading blueprints:', error);
        },
      }),
    );
  }

  onCreateBlueprint(): void {
    this.editingBlueprint = null;
    this.showBlueprintEditor = true;
  }

  onEditBlueprint(blueprint: Blueprint): void {
    this.editingBlueprint = blueprint;
    this.showBlueprintEditor = true;
  }

  onDeleteBlueprint(event: Event, blueprint: Blueprint): void {
    const blueprintName = blueprint.name;

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Are you sure you want to delete "${blueprintName}"? This action cannot be undone.`,
      header: `Delete ${blueprintName}`,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      accept: () => {
        this.deleteBlueprint(blueprint);
      },
    });
  }

  private deleteBlueprint(blueprint: Blueprint): void {
    this.subscriptions.add(
      this.quotesService.deleteBlueprint(blueprint.id).subscribe({
        next: () => {
          this.refreshBlueprints();
        },
        error: (error: unknown) => {
          if (isPermissionError(error)) return;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: parseApiError(error, 'Failed to delete blueprint'),
          });
          console.error('Error deleting blueprint:', error);
        },
      }),
    );
  }

  onBlueprintEditorClose(): void {
    this.showBlueprintEditor = false;
    this.editingBlueprint = null;
  }

  onSubmitBlueprint(data: {
    id: string | null;
    name: string;
    items: BlueprintItemWrite[];
    default_material_id: number | null;
  }): void {
    if (data.id) {
      // Update existing blueprint
      this.subscriptions.add(
        this.quotesService
          .updateBlueprint(
            data.id,
            data.name,
            data.items,
            data.default_material_id,
          )
          .subscribe({
            next: () => {
              // Refresh blueprints list
              this.refreshBlueprints();
              this.onBlueprintEditorClose();
            },
            error: (error: unknown) => {
              if (isPermissionError(error)) return;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: parseApiError(error, 'Failed to update blueprint'),
              });
              console.error('Error updating blueprint:', error);
            },
          }),
      );
    } else {
      // Create new blueprint
      this.subscriptions.add(
        this.quotesService
          .createBlueprint(data.name, data.items, data.default_material_id)
          .subscribe({
            next: () => {
              // Refresh blueprints list
              this.refreshBlueprints();
              this.onBlueprintEditorClose();
            },
            error: (error: unknown) => {
              if (isPermissionError(error)) return;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: parseApiError(error, 'Failed to create blueprint'),
              });
              console.error('Error creating blueprint:', error);
            },
          }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private refreshBlueprints(): void {
    this.subscriptions.add(
      this.quotesService.getBlueprints$(true).subscribe({
        next: (blueprints: Blueprint[]) => {
          this.allBlueprints = blueprints;
        },
        error: (error: unknown) => {
          if (isPermissionError(error)) return;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: parseApiError(error, 'Failed to load blueprints'),
          });
          console.error('Error loading blueprints:', error);
        },
      }),
    );
  }
}
